import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { MegaMenuNav } from "@/components/MegaMenuNav";
import { Footer } from "@/components/Footer";
import { CheckCircle2, ArrowRight, Mic, Brain, Video } from "lucide-react";
import { Helmet } from "react-helmet-async";

export default function InterviewPrepPage() {
  return (
    <>
      <Helmet>
        <title>AI Interview Prep - Practice with Conversational AI | LoopBaan.ai</title>
        <meta name="description" content="Practice interviews with AI that acts like a real interviewer. Get instant feedback, build confidence, and ace your interviews." />
      </Helmet>

      <div className="min-h-screen bg-background">
        <MegaMenuNav />

        <section className="relative overflow-hidden py-20 md:py-32 bg-gradient-to-br from-accent/10 via-background to-primary/10">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-6 bg-accent/20 text-accent border-accent/30">AI-POWERED</Badge>
              <h1 className="mb-6 text-5xl md:text-7xl font-bold leading-tight">
                Practice Interviews{" "}
                <span className="bg-gradient-to-r from-accent to-primary bg-clip-text text-transparent">
                  With AI
                </span>
              </h1>
              <p className="mb-8 text-xl text-muted-foreground max-w-2xl mx-auto">
                Conversational AI that acts like a real interviewer. Practice with your voice, get instant feedback, and build confidence.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/signup">
                  <Button size="lg" className="gap-2">
                    Start Practicing <ArrowRight className="h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid gap-8 md:grid-cols-3 max-w-6xl mx-auto">
              {[
                {
                  icon: Mic,
                  title: "Voice or Text Practice",
                  description: "Practice speaking your answers out loud or type them. AI analyzes both verbal and written responses."
                },
                {
                  icon: Brain,
                  title: "Real-Time Feedback",
                  description: "Get instant feedback on your answers: structure, content, clarity, and areas for improvement."
                },
                {
                  icon: Video,
                  title: "Company-Specific Prep",
                  description: "Practice questions tailored to specific companies and roles. Be ready for exactly what they'll ask."
                }
              ].map((feature, i) => (
                <Card key={i} className="p-6 hover:shadow-lg transition-all">
                  <feature.icon className="h-12 w-12 text-accent mb-4" />
                  <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-bold text-center mb-12">What You'll Practice</h2>
              <div className="grid gap-6 md:grid-cols-2">
                {[
                  "Behavioral interview questions",
                  "Technical coding challenges",
                  "System design discussions",
                  "Case study analysis",
                  "Leadership scenarios",
                  "Conflict resolution",
                  "Career goals and motivation",
                  "Salary negotiation"
                ].map((benefit, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                    <span className="text-lg">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl font-bold mb-8">Features</h2>
              <div className="grid gap-8 md:grid-cols-3">
                <div>
                  <div className="text-5xl font-bold text-accent mb-2">∞</div>
                  <div className="text-muted-foreground">Unlimited Practice</div>
                </div>
                <div>
                  <div className="text-5xl font-bold text-accent mb-2">10K+</div>
                  <div className="text-muted-foreground">Interview Questions</div>
                </div>
                <div>
                  <div className="text-5xl font-bold text-accent mb-2">24/7</div>
                  <div className="text-muted-foreground">Available Anytime</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Walk Into Interviews With Confidence
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Stop winging it. Practice with AI until you're ready for anything.
            </p>
            <Link to="/signup">
              <Button size="lg" className="gap-2">
                Start Practicing <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
